// world.config.js — Single source of truth for v1-studio.
//
// v1 ships a single studio room. The previous 7-room alpine-peninsula
// build is preserved verbatim in `world.config.future.js` and revived
// at v2+ per V1-STUDIO-SCOPE.md "North Star / Backlog".
//
// All content lives here. Scenes read from this file. Never hard-code
// content inside scene code.

export const WORLD_CONFIG = {
  // 16-pixel base tile, scale 2 → 32 px/tile display. Stardew/Sprout-Lands
  // cousin aesthetic. See HOUSE-DESIGN-BRIEF.md "Pixel-art rendering tier".
  tileSize: 16,
  scale: 2,
  defaultRoom: 'studio',
  transitionStyle: 'fade',
  droneSpeed: 140,
  cameraLerp: 0.1,
  transitionLockMs: 700,
};

export const INTRO_CONFIG = {
  name: 'Ian Standbridge',
  tagline: 'making things, mostly',
  bootLines: [
    'initializing world...',
    'loading memories...',
    'charting coordinates...',
    'ready.',
  ],
};

// ─── Rooms ──────────────────────────────────────────────────────────────
// v1 = single studio. The studio is image-backed (Path D rendering): once
// the studio shell PNG lands at `public/assets/scenes/studio.png`, the
// engine renders the AI shell with sprite overlays on top. Until the PNG
// is authored, falls back to programmatic interior-floor rendering so
// the engine stays runnable.
export const ROOMS = [
  {
    id: 'studio',
    label: 'Studio',
    // Path D — shell+overlay mode. When studio.png lands, the AI render
    // becomes the architectural shell (walls, floor, ceiling, window,
    // door — NO furniture). The OBJECTS array continues to render on
    // top as visible sprite overlays. See V1-STUDIO-SCOPE.md "shellMode"
    // and ROUND-3-AUDITION-INTERIORS.md "Round 4 — Shell + Overlay".
    backgroundImage: 'assets/scenes/studio.png',
    shellMode: true,
    widthTiles: 24,
    heightTiles: 16,
    floorColor: 0xC49A6C,           // walnut floor (sprite-fallback path)
    wallColor: 0x5A3A24,             // warm medium walnut walls (lighter than 0x3a2a1f
                                      // to feel cozy, distinct from the floor color)
    accent: '#f6b93b',
    spawnX: 12,
    spawnY: 10,                      // mid-room — post-fly-in drone lands near Ian
    doors: [],                        // no transitions in v1 — single room
  },
];

// ─── Objects (interactive) ──────────────────────────────────────────────
// Studio inventory per V1-STUDIO-SCOPE.md "Studio objects (interactive
// inventory)". 12 interactive objects + 3 decoratives. Projects scatter
// as diegetic objects (not clustered on a single worktable) so each gets
// its own discovery moment.
//
// Tile-coordinate layout (24 wide × 16 tall, walls on edges).
// Render `depth` field controls Z-order: lower = behind, higher = in front.
// Default depth in HouseScene is 5; surfaces stay at 5, items "on" surfaces
// use 6, walls/floors use 4.

export const OBJECTS = [
  // ─── Floor (rendered first, lowest depth) ────────────────────────────

  // Rug — sits under the Eames chair (chair anchor at y=6, rug at y=7 so
  // the chair sprite renders on top and looks like it's standing on the rug).
  {
    id: 'studioRug',
    room: 'studio',
    x: 4,
    y: 7,
    label: 'Rug',
    textureKey: 'gr-rug',
    accent: '#F3EAD5',
    depth: 4,
  },

  // ─── Surfaces (depth 5 — default) ────────────────────────────────────

  // Bookshelf — SW corner. 16×30 native (taller for visibility), spans
  // ~2 tiles vertically (sprite top ~y=11, bottom ~y=13).
  {
    id: 'bookshelf',
    room: 'studio',
    x: 2,
    y: 12,
    label: 'Bookshelf',
    promptText: 'Browse the shelf',
    type: 'modal',
    textureKey: 'studio-bookshelf',
    accent: '#e0a45a',
    content: {
      title: 'On the shelf',
      fields: {
        last_finished: 'Project Hail Mary',
        lego_sets_on_display: 'Lego Star Wars Trench Run',
        current_favorite_video_game: 'Mario Tennis Fever',
        board_game_corner: 'Catan',
      },
    },
  },

  // Worktable — Ian's hangout. Pure decoration now: the laptop on top of
  // it owns the "Peek at the screen" interaction, and pixel-Ian (the NPC
  // standing south of it) owns "Talk to Ian" via the invisible hotspot
  // below. Centered on x-axis (room is 24 wide; x=12 is the centerline).
  {
    id: 'worktable',
    room: 'studio',
    x: 12,
    y: 5,
    label: "Ian's worktable",
    textureKey: 'studio-worktable',
    accent: '#dcd0b8',
  },

  // Talk-to-Ian hotspot — invisible interactive object co-located with
  // pixel-Ian NPC at (12, 7). No textureKey, so no sprite is rendered;
  // ObjectManager just uses it for the E-prompt. The NPC sprite itself
  // is rendered by _drawNpcs. This keeps the "Talk to Ian" prompt on
  // the visible character instead of the desk.
  {
    id: 'talkToIan',
    room: 'studio',
    x: 12,
    y: 7,
    label: 'Ian',
    promptText: 'Talk to Ian',
    type: 'dialogue',
    dialogueKey: 'intro',
    repeatDialogueKey: 'introRepeat',
  },

  // ─── Wall-mounted (depth 5) ──────────────────────────────────────────

  // Remi — heart-shaped sketch pinned above the bookshelf.
  {
    id: 'projectRemi',
    room: 'studio',
    x: 5,
    y: 1,
    label: 'Remi',
    promptText: 'Open Remi',
    type: 'link',
    textureKey: 'studio-remi-sketch',
    accent: '#ff8fa3',
    url: 'https://www.withremi.app',
    content: {
      title: 'Remi',
      body: "A relationship app for couples. Most of us mean to be more thoughtful with the people we love and sometimes forget how long it's been since the last time we followed through. Remi turns good intentions into a system for your relationship. Building it for my own relationship and a few other beta users right now.",
    },
  },

  // Apartment Hunter — Chicago map with pushpins, framed on the wall.
  {
    id: 'projectAptHunter',
    room: 'studio',
    x: 15,
    y: 1,
    label: 'Apartment Hunter',
    promptText: 'Open Apartment Hunter',
    type: 'modal',
    textureKey: 'studio-apthunter-map',
    accent: '#7ad7d4',
    content: {
      title: 'Apartment Hunter',
      body: "A comparison tool for finding apartments. Started as a spreadsheet I built apartment-hunting in Chicago. Pulls price, size, commute, light, neighborhood feel, whatever matters to you, into one place and weighted-scores them so you can compare side by side. Used it twice: once with my old roommate, then with Layne to find our current place. Now I'm turning it into a product so other people can use it too.",
    },
  },

  // Framed photo on the north wall — links to Elise's site (Ian's little
  // sister). Whimsical "pop over next door" framing. Uses the link-confirm
  // flow (HouseScene.openLink) so visitors get a confirmation modal before
  // the new tab opens.
  {
    id: 'framedNeighborPhoto',
    room: 'studio',
    x: 20,
    y: 1,
    label: "Elise's site",
    promptText: 'Pop over next door',
    type: 'link',
    textureKey: 'studio-neighbor-photo',
    accent: '#9bd28a',
    url: 'https://elisestandbridge.com',
    content: {
      title: 'Elise, next door',
      body: "Elise is my little sister. She lives next door, basically. Pop over to her site whenever.",
    },
  },

  // TV — east wall, mid-height. Sits across the room from the Eames chair
  // for a "living room" composition (chair watches TV across the floor).
  {
    id: 'tv',
    room: 'studio',
    x: 22,
    y: 8,
    label: 'TV',
    promptText: 'Turn on the TV',
    type: 'tvPortfolio',
    textureKey: 'studio-tv',
    accent: '#4C468B',
  },

  // ─── Floor objects (depth 5) ─────────────────────────────────────────

  // Eames chair — west, between bookshelf and worktable. When the AI
  // overlay PNG (eames-chair.png) lands, the engine scales it to fit
  // ~3 tiles wide (chair + ottoman side by side, with aspect preserved).
  {
    id: 'eamesChair',
    room: 'studio',
    x: 5,
    y: 6,
    label: 'Eames chair',
    promptText: 'Sit a minute',
    type: 'modal',
    textureKey: 'gr-eames-chair',
    targetTileWidth: 3,
    accent: '#B85C2A',
    content: {
      title: 'The Eames Chair',
      body: "Another version of The Eames Chair I actually have at home. The one in my living room is black leather, this one ended up cognac. Either way, same idea: pulled up to the window, decent reading light, no laptop within arm's reach.",
    },
  },

  // Floor lamp — against the west wall just behind the Eames chair. Gives
  // the chair area a warm reading-light pool via the engine's
  // _drawAmbientGlows pass.
  {
    id: 'floorLamp',
    room: 'studio',
    x: 2,
    y: 6,
    label: 'Floor lamp',
    textureKey: 'gr-floor-lamp',
    accent: '#F6B93B',
    glow: { color: 0xF6B93B, radius: 96 },   // ~3 tile warm pool of light
  },

  // Snake plant — east-center of room. Has a modal because the real one
  // was Ian's first plant; the studio version is its digital twin.
  {
    id: 'snakePlantStudio',
    room: 'studio',
    x: 17,
    y: 11,
    label: 'Snake plant',
    promptText: 'Take a look',
    type: 'modal',
    textureKey: 'gr-snake-plant',
    accent: '#67835C',
    content: {
      title: 'Snake plant',
      body: "Digital twin of the first plant I ever owned. Yes, Sylvester is still alive at home.",
    },
  },

  // Caffeine Rush arcade cabinet — east wall, lower. AI overlay scales
  // to 1.5 tiles wide (taller than wide — standing cabinet shape).
  {
    id: 'arcadeCabinet',
    room: 'studio',
    x: 22,
    y: 11,
    label: 'Caffeine Rush',
    promptText: 'Play Caffeine Rush',
    type: 'link',
    textureKey: 'studio-arcade-cabinet',
    targetTileWidth: 1.5,
    accent: '#D85A30',
    url: 'https://caffeine.ianstandbridge.com',
    content: {
      title: 'Caffeine Rush, arcade cabinet',
      body: "Coin-op cabinet running Caffeine Rush. A fun little game I made out of curiosity about how caffeine actually works. Takes about two minutes to play, and you'll learn a thing or two about caffeine on the way through. The button opens it in a new tab.",
    },
  },

  // Mailbox — left of the door on the south wall. Was in the SE corner;
  // moved closer to the door to make room for sport-gear decor on the
  // door's right side.
  {
    id: 'mailbox',
    room: 'studio',
    x: 10,
    y: 14,
    label: 'Mailbox',
    promptText: 'Peek inside',
    type: 'mail',
    textureKey: 'studio-mailbox',
    accent: '#f6b93b',
  },

  // Sport gear — label-only objects (have `label` but no `type`, so they
  // show a name tag on proximity but no [E] prompt or popup).

  // Yoga mat — laid flat in the top-right corner, like it's set up ready
  // to use. Black mat (matches Ian's actual one at home).
  // `showLabel: true` opts into proximity name-tag without an [E] prompt.
  {
    id: 'yogaMat',
    room: 'studio',
    x: 20,
    y: 4,
    label: 'Yoga mat',
    showLabel: true,
    textureKey: 'studio-yoga-mat',
  },
  // Ball basket — soccer ball + beach volleyball tucked into a woven basket
  // by the door (right side, mirroring the mailbox-left layout).
  {
    id: 'ballBasket',
    room: 'studio',
    x: 16,
    y: 14,
    label: 'Soccer + volleyball',
    showLabel: true,
    textureKey: 'studio-ball-basket',
  },

  // ─── On-surface items (depth 6 — render on top of surfaces) ──────────

  // Laptop on the worktable — sits centered on the worktable tile (12, 5)
  // and becomes the main visual of that tile (depth: 6 renders above the
  // worktable surface). Pressing E opens an image modal showing Ian's
  // Teams background (a Lake Tahoe corner office he designed for a future
  // home). NOTE: shares a tile with the worktable's "Talk to Ian" prompt;
  // pixel-Ian NPC at (12, 7) is the primary dialogue trigger now.
  {
    id: 'worktableLaptop',
    room: 'studio',
    x: 12,
    y: 5,
    label: 'Laptop',
    promptText: 'Peek at the screen',
    type: 'modal',
    textureKey: 'studio-worktable-laptop',
    accent: '#7a9ec9',
    depth: 6,
    bob: true,
    pixelOffsetY: -14,
    content: {
      title: 'Eventually',
      image: '/assets/scenes/teams-bg-tahoe.png',
      body: "Designed this Lake Tahoe corner office for a future home. Doesn't exist yet, but a guy can dream. Using it as my Teams background in the meantime. And yes, the artwork on the wall is real.",
    },
  },

  // Bracket print — wall-mounted on the north wall. Wall gallery is evenly
  // spaced (5-tile gaps): Remi (5) → Perfect Bracket (10) → Apt Hunter (15)
  // → Elise (20).
  {
    id: 'projectBracket',
    room: 'studio',
    x: 10,
    y: 1,
    label: 'Perfect Bracket',
    promptText: 'Open Perfect Bracket',
    type: 'modal',
    textureKey: 'studio-bracket-print',
    accent: '#ffb84a',
    content: {
      title: 'Perfect Bracket',
      body: "I'm on a mission to build the world's first perfect bracket. Been refining a system for five years now, using historical trends and data to take some of the madness out of March Madness, narrowing the possibility space. Started as a way to win at least one of the bracket groups I'm in every year. Now I'm turning it into a product so other people can do the same.\n\nNot live yet. Check back closer to March Madness 2027.",
    },
  },
];

// ─── NPCs ────────────────────────────────────────────────────────────
// Pixel-Ian stands at the worktable, ready to greet visitors. The
// worktable's E-action also routes through the same dialogue, so visitors
// can re-trigger the conversation by interacting with either Ian or the
// worktable.
export const NPCS = [
  {
    id: 'pixelIan',
    room: 'studio',
    // Stands two tiles south of the worktable (worktable at x=12, y=5; Ian at x=12, y=7)
    // — gives him a bit of breathing room from the desk while still reading as "his".
    x: 12,
    y: 7,
    facing: 'down',
    triggerOnSpawn: true,
    dialogueKey: 'intro',
    repeatDialogueKey: 'introRepeat',
    textureKey: 'npc-pixel-ian',
  },
];

// ─── Dialogue ────────────────────────────────────────────────────────
// Rewritten for the v1 studio context. Direct, dry, understated, doesn't
// perform. The intro ends with a routing question that points visitors at
// the right object in the studio (no "downstairs" / "upstairs" since
// everything is in this one room).
export const DIALOGUE = {
  intro: [
    { speaker: 'IAN', text: "Oh hey." },
    { speaker: 'IAN', text: "You found me." },
    { speaker: 'IAN', text: "This is the studio. It's where I work, mostly." },
    {
      speaker: 'IAN',
      text: "What brings you here?",
      choices: [
        {
          label: "Show me what you're working on.",
          next: [
            { speaker: 'IAN', text: "Three things, mostly. The heart sketch on the wall is Remi. The bracket print on the wall is Perfect Bracket. The Chicago map next to it is Apartment Hunter." },
            { speaker: 'IAN', text: "Walk over to whichever pulls you in and hit E. Or open Remi or Caffeine Rush from the phone in the dock if you want the live ones right now." },
          ],
        },
        {
          label: "I came to play a game.",
          next: [
            { speaker: 'IAN', text: "Arcade cabinet's against the east wall. Caffeine Rush is the one I just shipped." },
            { speaker: 'IAN', text: "You can also tap it from the phone in the dock if you don't want to walk." },
          ],
        },
        {
          label: "Just exploring.",
          next: [
            { speaker: 'IAN', text: "Cool. Walk around, hit E on whatever catches your eye." },
          ],
        },
        {
          // Sentinel id keys this branch to the email-capture flow in HouseScene.
          // When this choice is picked, the dialogue plays out and the email
          // modal opens automatically when the dialogue closes.
          label: "I want to learn how to build something like this.",
          id: 'learn-to-build',
          next: [
            { speaker: 'IAN', text: "Oh nice." },
            { speaker: 'IAN', text: "I'm putting together a way for people to make their own. Not quite ready yet." },
            { speaker: 'IAN', text: "Want me to email you when it is?" },
          ],
        },
      ],
    },
  ],
  introRepeat: [
    { speaker: 'IAN', text: "You're back. Nice." },
    {
      speaker: 'IAN',
      text: "Anything in particular this time?",
      choices: [
        {
          label: "What's new?",
          next: [
            { speaker: 'IAN', text: "Tap the mailbox by the door (or in the dock). Anything new's in there." },
          ],
        },
        {
          label: "Just dropping in.",
          next: [
            { speaker: 'IAN', text: "Make yourself at home." },
          ],
        },
        {
          label: "Show me your work.",
          next: [
            { speaker: 'IAN', text: "Heart sketch on the wall is Remi. The bracket print on the wall is Perfect Bracket. The Chicago map next to it is Apartment Hunter." },
            { speaker: 'IAN', text: "Walk over and hit E on any of them. Phone's got Remi and Caffeine Rush if you want to skip ahead, and the mailbox by the door has anything new since you were last here." },
          ],
        },
      ],
    },
  ],
};

// Neighborhood is empty for now — Layne and Elise's houses aren't built
// as visitable surfaces yet. The framed photo in the studio teases this.
// When v6 (per V1-STUDIO-SCOPE.md roadmap) revives the neighborhood,
// populate this array.
export const NEIGHBORHOOD = [];

// ─── Phone apps ──────────────────────────────────────────────────────
// Pixel-rendered phone in the dock launches these. Each entry: id, label,
// textureKey for icon, and either url (opens new tab) or kind: 'recursive'
// (special: shows "you're already here" message instead of opening).
export const APPS = [
  {
    id: 'caffeineRush',
    label: 'Caffeine Rush',
    textureKey: 'app-caffeine-rush',
    url: 'https://caffeine.ianstandbridge.com',
    accent: '#7F77DD',
  },
  {
    id: 'remi',
    label: 'Remi',
    textureKey: 'app-remi',
    url: 'https://www.withremi.app',
    accent: '#3A332E',
    iconScale: 0.72,
  },
  {
    id: 'ianWorld',
    label: "Ian's World",
    textureKey: 'app-ian-world',
    kind: 'recursive',
    accent: '#f6b93b',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    textureKey: 'app-linkedin',
    url: 'https://www.linkedin.com/in/ian-standbridge/',
    accent: '#0A66C2',
  },
  {
    id: 'github',
    label: 'GitHub',
    textureKey: 'app-github',
    url: 'https://github.com/standbib',
    accent: '#181717',
  },
  {
    id: 'instagram',
    label: 'Instagram',
    textureKey: 'app-instagram',
    url: 'https://www.instagram.com/ianstandbridge/',
    accent: '#E1306C',
  },
  {
    id: 'spotify',
    label: 'Spotify',
    textureKey: 'app-spotify',
    url: 'https://open.spotify.com/user/ianstand',
    accent: '#1DB954',
  },
];

// ─── Mail inbox ──────────────────────────────────────────────────────
// First entry is the side-quest hint placeholder (always shown). Repeat-
// visit entries appear chronologically based on `since` ISO date —
// visitors see any whose `since` is after their last recorded visit.
export const MAIL = [
  {
    id: 'sideQuestHint',
    from: 'Ian',
    subject: 'Side quest, coming soon',
    preview: 'Hidden things in this studio. Check back here for hints.',
    body: "Hey. There's going to be a side quest hidden in this studio. I haven't built it yet.\n\nWhen it's ready, the hint will land here in the mailbox. If you're a return visitor and you've already read this, you'll see new mail above instead.\n\nIan",
    pinned: true,
  },
  {
    id: 'whatsNew-2026-05-06',
    from: 'Ian',
    subject: "New studio, May 2026",
    preview: "Just moved in. Take a look around.",
    body: "Welcome to the new studio. Just moved in.\n\nWhat's around:\n\n• Projects scatter as items around the room. Heart sketch is Remi, bracket print is Perfect Bracket, Chicago map is Apartment Hunter. Hit E on any of them\n• TV's got a few rows: favorite movies, favorite shows, and a Coming Soon row for video clips I'm working on\n• Framed photo on the wall opens Elise's site (my little sister, in case you didn't know)\n• Caffeine Rush is on the arcade cabinet in the back\n\nIan",
    since: '2026-05-06',
  },
];

// ─── Video portfolio ──────────────────────────────────────────────────
// TVPortfolioSystem reads from these arrays. Single Featured row at
// launch — add more categories when content density justifies splitting.
// embedKind: 'mp4' (R2 / self-hosted), 'vimeo', 'youtube', or 'link'.
//
// Hosting recommendation: Cloudflare R2 (zero egress fees) for self-hosted
// mp4s. See V1-STUDIO-SCOPE.md "Hosting" for the breakdown.
//
// v1 ships with: Favorite Movies + Favorite Shows + a single "Coming Soon"
// placeholder tile in the Featured Clips row. Real drone/video clips drop in
// later as Ian curates. Movies + shows link out to Letterboxd / IMDb.
//
// `embedKind: 'placeholder'` tiles are guarded in TVPortfolioSystem so they
// don't fire a click handler — they just sit there as "Coming Soon" cards.
export const VIDEO_PORTFOLIO = [
  // ─── Favorite Movies ──────────────────────────────────────────────
  { id: 'mv-interstellar', title: 'Interstellar', category: 'movies', thumbnail: '/assets/tv-thumbs/interstellar.avif', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/interstellar/' },
  { id: 'mv-eeaao', title: 'Everything Everywhere All at Once', category: 'movies', thumbnail: '/assets/tv-thumbs/eeaao.jpg', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/everything-everywhere-all-at-once/' },
  { id: 'mv-rots', title: 'Star Wars: Revenge of the Sith', category: 'movies', thumbnail: '/assets/tv-thumbs/revenge-of-the-sith.jpg', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/star-wars-episode-iii-revenge-of-the-sith/' },
  { id: 'mv-spiderverse', title: 'Spider-Man: Into the Spider-Verse', category: 'movies', thumbnail: '/assets/tv-thumbs/spider-verse.jpg', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/spider-man-into-the-spider-verse/' },
  { id: 'mv-phm', title: 'Project Hail Mary', category: 'movies', thumbnail: '/assets/tv-thumbs/project-hail-mary.jpg', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/project-hail-mary/' },
  { id: 'mv-walle', title: 'WALL-E', category: 'movies', thumbnail: '/assets/tv-thumbs/wall-e.avif', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/wall-e/' },
  { id: 'mv-cars', title: 'Cars', category: 'movies', thumbnail: '/assets/tv-thumbs/cars.jpg', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/cars/' },
  { id: 'mv-zootopia', title: 'Zootopia', category: 'movies', thumbnail: '/assets/tv-thumbs/zootopia.webp', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/zootopia/' },
  { id: 'mv-stepbros', title: 'Step Brothers', category: 'movies', thumbnail: '/assets/tv-thumbs/step-brothers.webp', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/step-brothers/' },
  { id: 'mv-otherguys', title: 'The Other Guys', category: 'movies', thumbnail: '/assets/tv-thumbs/the-other-guys.webp', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/the-other-guys/' },
  { id: 'mv-superbad', title: 'Superbad', category: 'movies', thumbnail: '/assets/tv-thumbs/superbad.jpg', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/superbad/' },
  { id: 'mv-rpo', title: 'Ready Player One', category: 'movies', thumbnail: '/assets/tv-thumbs/ready-player-one.jpg', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/ready-player-one/' },
  { id: 'mv-pitchperfect', title: 'Pitch Perfect', category: 'movies', thumbnail: '/assets/tv-thumbs/pitch-perfect.jpg', embedKind: 'link', embedUrl: 'https://letterboxd.com/film/pitch-perfect/' },

  // ─── Favorite Shows ───────────────────────────────────────────────
  { id: 'sh-tedlasso', title: 'Ted Lasso', category: 'shows', thumbnail: '/assets/tv-thumbs/ted-lasso.jpg', embedKind: 'link', embedUrl: 'https://www.imdb.com/title/tt10986410/' },
  { id: 'sh-mandalorian', title: 'The Mandalorian', category: 'shows', thumbnail: '/assets/tv-thumbs/the-mandalorian.jpg', embedKind: 'link', embedUrl: 'https://www.imdb.com/title/tt8111088/' },
  { id: 'sh-got', title: 'Game of Thrones', category: 'shows', thumbnail: '/assets/tv-thumbs/game-of-thrones.png', embedKind: 'link', embedUrl: 'https://www.imdb.com/title/tt0944947/' },
  { id: 'sh-rickmorty', title: 'Rick and Morty', category: 'shows', thumbnail: '/assets/tv-thumbs/rick-and-morty.jpg', embedKind: 'link', embedUrl: 'https://www.imdb.com/title/tt2861424/' },

  // ─── Featured Clips (placeholder until real content lands) ────────
  { id: 'clip-comingsoon', title: 'Coming Soon', category: 'clips', description: "Drone footage and video work I've made. I'll start dropping clips here as I curate.", embedKind: 'placeholder' },
];

export const VIDEO_PORTFOLIO_CATEGORIES = [
  { id: 'movies', label: 'Favorite Movies' },
  { id: 'shows', label: 'Favorite Shows' },
  { id: 'clips', label: 'Featured Clips' },
];
